import BaseObject from '../../base/base_object';
import Device from '../../utils/device/device';
import Event, {SystemEventSubscription} from '../../utils/event';
import {SYSTEM_EVENTS} from '../../events/events';
import {
    IGameInfoFromBackend,
    ExternalGameSubTypeInfo,
    ExternalGameTypeInfo
} from './games_information_service';
import GamesDiagnosticMessages from './games_diagnostic_messages';
import ServiceFactory from '../service_factory';
import {ILocalizedGameInfo, PLATFORM_TYPES, GAMES_INFO} from './games_information_service_interface';
import _ from '../../utils/underscore';
import { ILiveTableInfo } from '../live_tables/live_table_info';
import {Check} from '@sparkware/app-utils';

export default class GamesInfoCache extends BaseObject {
    static CLASS = 'GamesInfoCache';
    private _getServiceFactory: () => ServiceFactory;
    private _gamesDiagnostics: GamesDiagnosticMessages;
    private _onRestrictionsChangedSubscription: SystemEventSubscription;
    private _onOAuthStateChangedSubscription: SystemEventSubscription;
    private _onInitCompletedSubscription: SystemEventSubscription;
    private _shouldUpdateBetsFromLiveTables: boolean = false;
    private _previousRegulationIDForBets: number = null;

    init(params) {
        super.init(params);
        this._getServiceFactory = params.getServiceFactory;
        this._gamesDiagnostics = params.gamesDiagnostics;
        this._setActivePlatform();
        this._subscribe();
    }

    dispose() {
        this._unsubscribe();
    }

    private _subscribe() {
        this._onRestrictionsChangedSubscription = Event.on(SYSTEM_EVENTS.RESTRICTIONS_CHANGED, this._onRestrictionsChanged, this);
        this._onOAuthStateChangedSubscription = Event.on(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOAuthStateChanged, this);
        this._onInitCompletedSubscription = Event.on(SYSTEM_EVENTS.ON_INIT_COMPLETE, this._onInitComplete, this);
    }

    _onInitComplete() {
        if (this.serviceFactory.skinService().getRegulationID() !== this.serviceFactory.liveTablesService().getRegulationIDForMinMaxBet()
            && this.serviceFactory.oAuth2Service().isAuthorized()) {
            this._shouldUpdateBetsFromLiveTables = true;
        }

        this._previousRegulationIDForBets = this.serviceFactory.liveTablesService().getRegulationIDForMinMaxBet();
    }

    private _unsubscribe() {
        this._onRestrictionsChangedSubscription.off();
        this._onOAuthStateChangedSubscription.off();
        this._onInitCompletedSubscription.off();
    }

    private _onRestrictionsChanged() {
        Object.keys(this._gamesInfoCache).forEach(key => {
            this._gamesInfoCache[key].restriction = null;
        });
    }

    private _onOAuthStateChanged() {
        if (this._previousRegulationIDForBets !== this.serviceFactory.liveTablesService().getRegulationIDForMinMaxBet()) {
            this._previousRegulationIDForBets = this.serviceFactory.liveTablesService().getRegulationIDForMinMaxBet();
            this._shouldUpdateBetsFromLiveTables = true;
        }

        Object.keys(this._gamesInfoCache).forEach(gameType => {
            const gameInfo = this._gamesInfoCache[gameType];
            const gameFromBackend = this._gamesFromBackend[gameType];
            gameInfo.isAnonymousSupported = (gameFromBackend.AllowDemo || false) && this.serviceFactory.regulationService().isAnonymousPlayAllowed();
            gameInfo.forceDemoButton = this.serviceFactory.regulationService().forceDemoButton();
            gameInfo.minBet = this._extractMinBetLimit(gameFromBackend.Bets, gameFromBackend.MaxBetPerBrand) // putting here gameFromBackend.MaxBetPerBrand is intentional because if we are in Germany and MaxBetPerBrand is not set in SDM then we don't want to show the min bet either
            gameInfo.maxBet = this._extractMaxBetLimit(gameFromBackend.Bets, gameFromBackend.MaxBetPerBrand)
        });
    }

    get serviceFactory() {
        return this._getServiceFactory();
    }

    private _gamesFromBackend: { [key: number]: IGameInfoFromBackend } = {};

    appendBackendGamesInfo(gamesFromBackend: IGameInfoFromBackend[]) {
        gamesFromBackend = gamesFromBackend || [];

        gamesFromBackend.forEach(gameInfo => {
            this._gamesFromBackend[gameInfo.Id] = gameInfo;
        });
    }

    private _gamesInfoCache: { [key: number]: ILocalizedGameInfo } = {};

    private _gamesSubTypeToGameTypeMappingCache: { [key: string]: number } = {};

    private _shouldUseLiveTableFeeds() {
        return this._shouldUpdateBetsFromLiveTables && this.serviceFactory.liveTablesService().getBetLimitsFromTableFeedsEnabled();
    }

    async getGamesInfo(gameTypes: number[]): Promise<{ gamesInfo: GAMES_INFO, notFoundGames: number[] }> {
        const gamesInfo = {};
        const notFoundGames = [];
        gameTypes = gameTypes || [];

        if (gameTypes.length === 0) {
            return Promise.resolve({
                gamesInfo: gamesInfo,
                notFoundGames: notFoundGames,
            });
        }


        let liveTables: {[id: number]: ILiveTableInfo} = null;
        if (this._shouldUseLiveTableFeeds()) {
            this._shouldUpdateBetsFromLiveTables = false;
            try {
                liveTables = await this.serviceFactory.liveTablesService().getTablesInfo();
            } catch(err) {
                this.logger.error("LiveTablesService::getTablesInfo failed!", err);
            }
        }

        gameTypes.forEach(gameType => {
            try {
                let gameInfo = this._gamesInfoCache[gameType];

                if (gameInfo) {
                    gamesInfo[gameType] = gameInfo;
                } else {
                    gameInfo = this._createGameInfoFromBackendInfo(gameType);
                    if (gameInfo) {
                        gamesInfo[gameType] = gameInfo;
                    } else {
                        notFoundGames.push(gameType);
                    }
                }

                if (gameInfo && !_.isNullOrUndefined(liveTables) && !_.isNullOrUndefined(liveTables[gameType])) {
                    const liveTable = liveTables[gameType];
                    gameInfo.minBet = liveTable.MinBet;
                    gameInfo.maxBet = liveTable.MaxBet;
                }
            } catch (e) {
                this.logger.error('Failed to get games info for gameType: ', gameType, ',Error: ', e);
            }
        });

        return Promise.resolve({
            gamesInfo: gamesInfo,
            notFoundGames: notFoundGames,
        });

    }

    getGamesSubTypesMappings(gameSubTypes?: string[]): { [key: string]: number } {
        if (!gameSubTypes || !_.isArray(gameSubTypes)) {
            return {...this._gamesSubTypeToGameTypeMappingCache};
        }

        const res: { [key: string]: number } = {};
        gameSubTypes.forEach(subType => {
            const cgpGameId = this._gamesSubTypeToGameTypeMappingCache[subType];
            if (cgpGameId) {
                res[subType] = cgpGameId;
            }
        });
        return res;
    }

    /**
     *
     * @param gameType
     * @returns {*}
     * @private
     */
    private _createGameInfoFromBackendInfo(gameType: number): ILocalizedGameInfo {
        const gameFromBackend = this._gamesFromBackend[gameType];
        if (!gameFromBackend) {
            return null;
        }

        const gameInfo: ILocalizedGameInfo = {
            gameType: gameFromBackend.Id,
            externalGameCode: this._extractExternalGameCode(gameFromBackend.ExtId),
            externalGameSubType: this._extractExternalSubTypeGameCode(gameFromBackend.ExtSubType),
            title: gameFromBackend.Title,
            isEliteLounge: gameFromBackend.IsEliteLounge,
            allowWithdrawOnlyFromReal: gameFromBackend.AllowWithdrawOnlyFromReal && this.serviceFactory.regulationService().getRegulation().supportWithdrawOnlyFromReal(),
            hasJackpot: gameFromBackend.HasJackpot && !this.serviceFactory.jackpotFeedsService().jackpotFeedsDisabled(),
            vendorName: gameFromBackend.Vendor,
            companyName: gameFromBackend.Company,
            gameGroup: gameFromBackend.Group,
            gameGroupId: gameFromBackend.GroupId,
            isAnonymousSupported: (gameFromBackend.AllowDemo || false) && this.serviceFactory.regulationService().isAnonymousPlayAllowed(),
            forceDemoButton: this.serviceFactory.regulationService().forceDemoButton(),
            forceIntermediatePageForAutorizedUsers: this.serviceFactory.regulationService().forceIntermediatePageForAutorizedUsers(),
            subGroupName: gameFromBackend.SubGroup,
            numberOfLines: gameFromBackend.NumberOfLines,
            hasFreeSpins: gameFromBackend.HasFreeSpins,
            allowedPlatformType: gameFromBackend.PlatformType,
            hasSideBets: gameFromBackend.HasSideBets,
            isMultiHand: gameFromBackend.IsMultiHand,
            maxBetPerSpot: gameFromBackend.MaxBetPerSpot,
            numberOfDecks: gameFromBackend.Decks,
            hasBonusGames: gameFromBackend.HasBonusGames,
            minBet: this._extractMinBetLimit(gameFromBackend.Bets, gameFromBackend.MaxBetPerBrand), // putting here gameFromBackend.MaxBetPerBrand is intentional because if we are in Germany and MaxBetPerBrand is not set in SDM then we don't want to show the min bet either
            maxBet: this._extractMaxBetLimit(gameFromBackend.Bets, gameFromBackend.MaxBetPerBrand),
            images: gameFromBackend.Images,
            approvals: gameFromBackend.Approvals,
        };

        if (gameInfo.externalGameSubType) {
            this._gamesSubTypeToGameTypeMappingCache[gameInfo.externalGameSubType] = gameInfo.gameType;
        }

        this._applyPlatformRestrictions(gameInfo);

        this._gamesInfoCache[gameType] = gameInfo;
        return gameInfo;
    }

    private _extractMinBetLimit(betsConfiguration: {[currency: string] : number[]}, maxBetPerBrand: number | null | undefined): number{
        if(_.isNullOrUndefined(maxBetPerBrand)){
            if(this.serviceFactory.regulationService().shouldHaveBetLimitsPerBrand()) {
                return null;
            }
         }

        return this._extractBetLimit(betsConfiguration, 0);
    }

    private _extractMaxBetLimit(betsConfiguration, maxBetPerBrand: number | null | undefined): number{
        return this.serviceFactory.regulationService().shouldHaveBetLimitsPerBrand()
                    ? maxBetPerBrand : this._extractBetLimit(betsConfiguration, 1);
    }

    /**
     * Extract min or max bet value from betsConfiguration
     * @param betsConfiguration it is a map where the key is the currency ISO3 symbol and the value is an array of 2 items the first one being minBet and the second is the maxBet
     * @param betType: 0 - minBet; 1 - maxBet
     * @private
     */
    private _extractBetLimit(betsConfiguration: {[currency: string]: number[]}, betType: 0 | 1): number {
        if (!betsConfiguration) {
            return null;
        }

        const betLimits = betsConfiguration[this._getActiveCurrency()];
        return betLimits && betLimits[betType]
    }

    private _extractExternalGameCode(typeInfo: ExternalGameTypeInfo): string | null | undefined {
        if (Check.isArray(typeInfo)) {
            return typeInfo[this.activePlatformIndex]
        }

        return typeInfo;
    }

    private _extractExternalSubTypeGameCode(subTypeInfo: ExternalGameSubTypeInfo): string | null | undefined {
        if (Check.isArray(subTypeInfo)) {
            return subTypeInfo[this.activePlatformIndex];
        }
        return subTypeInfo;
    }


    private _activeCurrency: string = null;
    /**
     * @returns {string}
     * @private
     */
    private _getActiveCurrency() {
        return this._activeCurrency || (this._activeCurrency = this.serviceFactory.currencyService().getCurrency().toUpperCase());
    }

    private _activePlatform: string = null;
    private _activePlatformIndex: number = null;
    get activePlatformIndex(): number {
        return this._activePlatformIndex;
    }

    /**
     * @returns {string}
     * @private
     */
    private _setActivePlatform() {
        if (Device.os.isMobileOS) {
            this._activePlatform = PLATFORM_TYPES.MOBILE;
            this._activePlatformIndex = 1;
        } else {
            this._activePlatform = PLATFORM_TYPES.PC;
            this._activePlatformIndex = 0;
        }
    }



    /**
     * @param platformType
     * @returns {boolean}
     * @private
     */
    private _isPlatformMatch(platformType: string) {
        switch (platformType) {
            case PLATFORM_TYPES.ALL:
                return true;
            case PLATFORM_TYPES.MOBILE:
                return this._activePlatform === PLATFORM_TYPES.MOBILE;
            case PLATFORM_TYPES.PC:
                return this._activePlatform === PLATFORM_TYPES.PC;
            case PLATFORM_TYPES.NONE:
                return false;
            default:
                this.throwError('319', `Invalid platform type ${platformType}`);
                return false;
        }
    }

    /**
     * @param gameInfo
     * @returns {boolean}
     * @private
     */
    private _isGameAllowedForCurrentPlatform(gameInfo) {
        return this._isPlatformMatch(gameInfo.allowedPlatformType);
    }

    private _applyPlatformRestrictions(gameInfo) {
        if (this._isGameAllowedForCurrentPlatform(gameInfo)) {
            gameInfo.isCurrentPlatformAllowed = true;
        } else {
            gameInfo.isCurrentPlatformAllowed = false;
            this._gamesDiagnostics.logGameFilteredByPlatformType(gameInfo.gameType, `Game "${gameInfo.gameType} - ${gameInfo.title}" is not returned because of platform type filtering! Game allowed platform is ${gameInfo.allowedPlatformType} and current detected platform is ${this._activePlatform}`);
        }
    }
}
